import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import heatmap from 'highcharts/modules/heatmap';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { Solution, SolutionPackage } from '../../models/Models';
import { useAppContext } from '../../contexts/AppContext';
dayjs.extend(dayOfYear);

heatmap(Highcharts);
highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
noDataToDisplay(Highcharts);

type Props = {
  name: string;
  data: SolutionPackage;
};

const HeatMapChart = ({ name, data }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState(null);
  const chartComponent = useRef(null);

  useEffect(() => {
    if (data) {
      prepareHighcharts(prepareData());
    }
  }, [data, name]);

  const prepareData = () => {
    let timeseries_data = [];

    const maxNPV = data.solutions.reduce((max, solution) => {
      return Math.max(max, solution.net_present_value);
    }, 0);

    data.solutions.forEach((solution: Solution, index) => {
      timeseries_data.push([solution?.num_cleanings, solution?.num_devices, solution?.net_present_value]);
    });
    return {
      name: 'NPV',
      borderWidth: 1,
      data: timeseries_data,
      dataLabels: {
        enabled: true,
        allowOverlap: true,
        useHTML: true,
        formatter: function () {
          if (this.point.value === maxNPV) {
            return '<span style="opacity: 1 !important;">🏆</span>';
          }
          return this.point.value;
        },
        style: {
          textAlign: 'center',
          color: '#000',
          fontSize: '20px',
        },
      },
    };
  };

  const prepareHighcharts = (highChartsData) => {
    const transformHeatmapData = (highChartsData, xCategories, yCategories) => {
      const xIndexMap = xCategories.reduce((acc, val, idx) => {
        acc[val] = idx;
        return acc;
      }, {});

      const yIndexMap = yCategories.reduce((acc, val, idx) => {
        acc[val] = idx;
        return acc;
      }, {});

      highChartsData.data = highChartsData.data.map(([x, y, value]) => {
        const xIndex = xIndexMap[x];
        const yIndex = yIndexMap[y];
        return [xIndex, yIndex, value];
      });
      return highChartsData;
    };

    const uniqueNumCleanings = Array.from(new Set(data.solutions.map((solution) => solution.num_cleanings))).sort((a, b) => a - b);
    const uniqueNumDevices = Array.from(new Set(data.solutions.map((solution) => solution.num_devices))).sort((a, b) => a - b);
    const transformedData = transformHeatmapData(highChartsData, uniqueNumCleanings, uniqueNumDevices);

    const maxNPV = data.solutions.reduce((max, solution) => {
      return Math.max(max, solution.net_present_value);
    }, 0);

    const chart = {
      chart: {
        type: 'heatmap',
        marginTop: 40,
        marginBottom: 80,
        plotBorderWidth: 1,
      },
      title: {
        text: name, // (' + Math.round(data?.average_soiling_ratio*1000)/1000 + ')',
        align: 'left',
      },
      xAxis: {
        title: {
          text: 'Number of Cleanings',
        },

        categories: uniqueNumCleanings, // Optional: control which ticks are shown
      },
      yAxis: {
        title: {
          text: 'Number of Devices',
        },
        categories: uniqueNumDevices,
        // reversed: true
      },
      colorAxis: {
        min: 0,
        max: maxNPV,
        stops: [
          [0.0, '#ec2e10'],
          [0.25, '#f06d2f'],
          [0.5, '#f7b32b'],
          [0.75, '#f7e967'],
          [0.79, '#d6f77b'],
          [0.82, '#8be38b'],
          [0.85, '#4ecdc4'],
          [0.88, '#90CAF9'],
          [0.91, '#2196F3'],
          [0.94, '#1565C0'],
          [0.97, '#2b485c'],
          [1.0, '#0B132B'],
        ],
        reversed: false,
      },
      legend: {
        align: 'right',
        layout: 'vertical',
        margin: 0,
        verticalAlign: 'top',
        y: 25,
        symbolHeight: 280,
      },
      tooltip: {
        formatter: function () {
          return (
            `<b>${Math.round(this.point.value / 10000) / 100} (M$)</b> NPV gain for ` +
            `<b>${this.series.xAxis.categories[this.point.x]}</b> cleanings<br>` +
            `and <b>${this.series.yAxis.categories[this.point.y]}</b> devices`
          );
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: [transformedData],
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ /*display: 'flex',*/ justifyContent: 'center', pl: 1, pr: 1 }}>
        {chartOptions ? ( // Render only when chartOptions is not null
          <HighchartsReact
            ref={chartComponent}
            containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
            highcharts={Highcharts}
            options={chartOptions}
          />
        ) : (
          <Typography color="text.secondary">Loading chart...</Typography>
        )}
      </Box>
    </>
  );
};

export default HeatMapChart;
